"use client";

import { useMemo } from "react";

import {
  resolveSparklineLayout,
  type SparklineLayout,
} from "./density-sparkline-math";

/**
 * State-driven palette for the sparkline bars. Tokens picked to align
 * with the per-row states from spec §6.3:
 *  - idle     : muted (default off-state)
 *  - active   : violet accent (the track is currently soloed)
 *  - disabled : very muted (drum / vocal tracks)
 *  - checked  : medium-light (the user picked this track, but it's not
 *               playing right now)
 */
const BAR_COLOR: Record<DensitySparklineVariant, Record<DensitySparklineState, string>> = {
  track: {
    idle: "rgba(255,255,255,0.18)",
    active: "var(--color-accent)",
    disabled: "rgba(255,255,255,0.09)",
    checked: "rgba(255,255,255,0.42)",
  },
  global: {
    idle: "rgba(255,255,255,0.18)",
    active: "var(--color-accent)",
    disabled: "rgba(255,255,255,0.09)",
    checked: "rgba(255,255,255,0.42)",
  },
};

type DensitySparklineVariant = "track" | "global";
type DensitySparklineState = "idle" | "active" | "disabled" | "checked";

interface DensitySparklineProps {
  /** Raw note-on counts per bucket. */
  buckets: readonly number[];
  /** Total SVG width in pixels. */
  width: number;
  /** Total SVG height in pixels. */
  height: number;
  /** Bar density variant — affects layout geometry only. */
  variant: DensitySparklineVariant;
  /** Soloed and currently playing. */
  isActive?: boolean;
  /** Disabled track (drums / vocal). */
  isDisabled?: boolean;
  /** Selected by the user (checkbox on) — only used when not active. */
  isChecked?: boolean;
  /**
   * When true the SVG stretches to fill its container width (`width` becomes
   * the internal coordinate space only). Used by the full-width global bar so
   * it can't overflow a narrower modal; the fixed-width track bars leave it off.
   */
  fluid?: boolean;
}

function resolveState(
  isActive: boolean,
  isDisabled: boolean,
  isChecked: boolean,
): DensitySparklineState {
  if (isActive) {
    return "active";
  }

  if (isDisabled) {
    return "disabled";
  }

  if (isChecked) {
    return "checked";
  }

  return "idle";
}

/** Track variant: 24 bars × 2px wide, 1px gap, centred in the SVG width. */
function renderTrackBars(
  layout: SparklineLayout,
  width: number,
  height: number,
  color: string,
): React.ReactNode {
  const BAR_WIDTH = 2;
  const BAR_GAP = 1;

  if (layout.kind === "centred") {
    const x = (width - BAR_WIDTH) / 2;
    const y = height - layout.height;

    return (
      <rect
        x={x}
        y={y}
        width={BAR_WIDTH}
        height={layout.height}
        rx={1}
        ry={1}
        fill={color}
      />
    );
  }

  const totalWidth = layout.heights.length * BAR_WIDTH + (layout.heights.length - 1) * BAR_GAP;
  const offsetX = (width - totalWidth) / 2;

  return (
    <>
      {layout.heights.map((h, i) => {
        const x = offsetX + i * (BAR_WIDTH + BAR_GAP);
        const y = height - h;

        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={BAR_WIDTH}
            height={h}
            rx={1}
            ry={1}
            fill={color}
          />
        );
      })}
    </>
  );
}

/**
 * Global variant: thin area-chart-ish bars filling the available width.
 * Width per bar = floor(width / N), no gap, so a 60-bucket pass fills the
 * whole bar without visible columns.
 */
function renderGlobalBars(
  layout: SparklineLayout,
  width: number,
  height: number,
  color: string,
): React.ReactNode {
  if (layout.kind === "centred") {
    const barW = 2;
    const x = (width - barW) / 2;
    const y = height - layout.height;

    return (
      <rect
        x={x}
        y={y}
        width={barW}
        height={layout.height}
        rx={1}
        ry={1}
        fill={color}
      />
    );
  }

  const barW = width / layout.heights.length;

  return (
    <>
      {layout.heights.map((h, i) => {
        const x = i * barW;
        const y = height - h;

        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={Math.max(1, barW - 0.5)}
            height={h}
            fill={color}
            opacity={0.85}
          />
        );
      })}
    </>
  );
}

export function DensitySparkline({
  buckets,
  width,
  height,
  variant,
  isActive = false,
  isDisabled = false,
  isChecked = false,
  fluid = false,
}: DensitySparklineProps) {
  const layout = useMemo(
    () => resolveSparklineLayout(buckets, height),
    [buckets, height],
  );

  const state = resolveState(isActive, isDisabled, isChecked);
  const color = BAR_COLOR[variant][state];

  return (
    <svg
      width={fluid ? "100%" : width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={fluid ? "none" : undefined}
      role="img"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      {variant === "track"
        ? renderTrackBars(layout, width, height, color)
        : renderGlobalBars(layout, width, height, color)}
    </svg>
  );
}
